import * as React from 'react';
import { useFormikContext } from 'formik';
import { TextInput } from '../../../../widgets/FormikInputFieldV2';
import { OBHardwareIPConfig, StrictSetFieldValue } from '../../../../types';
import { useIPAutoConfig } from './useIPAutoConfig';
import ipUtils from '../../ipUtils';
import { Panel } from '@rtkwlf/ui';
import styled from 'styled-components';
import { useBeforeUnloadConfirmation } from '../../hooks/useBeforeUnloadConfirmation';

const INPUT_PROPS_ON_IP_AUTO_CONFIG = {
  disabled: true,
  placeholder: 'DHCP override',
};

const IPConfigPanel: typeof Panel = styled(Panel)`
  max-width: 565px;
  margin-left: 0px;
  margin-right: 0px;
  margin-top: 30px;
`;

export const MANUAL_IP_CONFIG_FIELDS: Array<keyof OBHardwareIPConfig> = [
  'ipAddress',
  'subnetMask',
  'gateway',
  'primaryDns',
  'secondaryDns',
];

type Props = {
  disabled?: boolean;
};

/**
 * Form inputs related to manual hardware IP settings:
 *  - ipAddress
 *  - subnetMask
 *  - gateway
 *  - primaryDns
 *  - secondaryDns
 *  - isIPAutoConfig (when true, disregard all above fields; has confirm modal)
 * Must be used within a formik context.
 */
export const IPConfigFormInputs = ({ disabled }: Props) => {
  useBeforeUnloadConfirmation(true);
  const formikContext = useFormikContext<OBHardwareIPConfig>();
  const {
    values: { isIPAutoConfig },
    setFieldTouched,
  } = formikContext;
  const setFieldValue: StrictSetFieldValue<OBHardwareIPConfig> =
    formikContext.setFieldValue;

  const manualIpConfigProps = isIPAutoConfig
    ? INPUT_PROPS_ON_IP_AUTO_CONFIG
    : null;

  if (disabled && manualIpConfigProps) {
    manualIpConfigProps.disabled = true;
  }

  // clear & disable manual IP config fields if using auto config
  React.useEffect(() => {
    if (isIPAutoConfig) {
      MANUAL_IP_CONFIG_FIELDS.forEach((field) => {
        setFieldValue(field, '');
        setFieldTouched(field, false);
      });
    }
  }, [setFieldTouched, setFieldValue, isIPAutoConfig]);

  const { ipAutoConfigCheckbox, ipAutoConfigConfirmModal } = useIPAutoConfig();

  return (
    <IPConfigPanel title="IP Configuration" titleLevel={5}>
      <TextInput
        name="ipAddress"
        label="Static IP Address"
        {...manualIpConfigProps}
        required
        description="IPv4 address"
        warningFunc={ipUtils.isIPPrivate}
      />

      {ipAutoConfigCheckbox}

      <TextInput
        name="subnetMask"
        label="Subnet Mask"
        {...manualIpConfigProps}
        required
      />
      <TextInput
        name="gateway"
        label="Gateway"
        {...manualIpConfigProps}
        required
      />

      <TextInput
        name="primaryDns"
        label="Primary DNS Server"
        {...manualIpConfigProps}
        required
      />
      <TextInput
        name="secondaryDns"
        label="Secondary DNS Server"
        {...manualIpConfigProps}
      />

      {ipAutoConfigConfirmModal}
    </IPConfigPanel>
  );
};
